import type { Incident, IncidentStatus } from '../../incidents/types/incident.types'
import { formatClockTime } from '../utils/liveOpsAggregate'

const STATUS_CONFIG: Record<IncidentStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  NEW: { label: 'New', className: 'bg-purple-100 text-purple-700' },
  ACKNOWLEDGE: { label: 'Acknowledge', className: 'bg-yellow-100 text-yellow-700' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  BLOCKED: { label: 'Blocked', className: 'bg-red-100 text-red-700' },
  DONE: { label: 'Done', className: 'bg-green-100 text-green-700' },
  CLOSED: { label: 'Closed', className: 'bg-gray-100 text-gray-500' },
}

function StatusBadge({ status }: { status: IncidentStatus }): JSX.Element {
  const config = STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs font-medium ${config.className}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {config.label}
    </span>
  )
}

interface LiveFeedProps {
  incidents: Incident[]
}

export function LiveFeed({ incidents }: LiveFeedProps): JSX.Element {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[18px] font-bold text-gray-900">Security Tasks</h2>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-[14px]">
        <thead>
          <tr className="border-b border-[#E5E7EB] text-left text-xs font-bold uppercase tracking-wider text-gray-500">
            <th className="pb-2 pr-4">Time</th>
            <th className="pb-2 pr-4">Location</th>
            <th className="pb-2 pr-4">Task</th>
            <th className="pb-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {incidents.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-6 text-center text-sm text-gray-400">
                No security tasks
              </td>
            </tr>
          ) : (
            incidents.map((incident) => (
              <tr key={incident.id} className="border-b border-[#E5E7EB] last:border-0">
                <td className="py-2.5 pr-4 text-gray-500">{formatClockTime(incident.created_at)}</td>
                <td className="py-2.5 pr-4 text-gray-700">{incident.location ?? '—'}</td>
                <td className="py-2.5 pr-4 text-gray-700">{incident.title}</td>
                <td className="py-2.5">
                  <StatusBadge status={incident.status} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}
